import type { SchemaData } from './types'
import type { MapSchemaV2 } from './v2/types'
import { mapAssetUrl } from './mapSet'
import { fetchMapAsset, getMapBundleSession } from '../mapDelivery/defaultMapBundleSession'
import { adaptSchemaV2 } from '../../scripts/mapSchemaV2.mjs'

/**
 * Fetches the London artifact of the map bundle this page uses — the bundled
 * `/map-v2/schema.json` or a downloaded one (`fetchMapAsset`, TASK-1123) —
 * and adapts it
 * onto `SchemaData` (`adaptSchemaV2`, shared with the prerender scripts),
 * together with the slugs v2 retired from `/map-v2/legacy-ids.json` (TASK-986,
 * ~6 KB): a slug in a URL or in storage resolves the moment the schema does.
 * An unreadable table costs only the aliases, never the schema.
 *
 * MVP-level: no schema validation (zod/typia) — we trust the backend contract
 * and narrow via the TypeScript types in `./v2/types`. A later phase can wrap
 * the parsed value in a runtime validator if field drift becomes a real risk.
 *
 * No module-level cache: the browser's HTTP cache already dedupes repeat
 * fetches of the static file. AppShell + StationPanel each issue their own
 * `fetchSchema()` call on mount, browser returns the cached response —
 * negligible overhead, keeps tests side-effect free.
 */
export async function fetchSchema(): Promise<SchemaData> {
  const [res, retiredSlugs] = await Promise.all([fetchMapAsset('schema.json'), fetchRetiredSlugs()])
  if (!res.ok) {
    throw new Error(`fetchSchema: schema.json returned ${res.status} ${res.statusText}`)
  }
  try {
    return { ...adaptSchemaV2((await res.json()) as MapSchemaV2), retiredSlugs }
  } catch (err: unknown) {
    // A schema that passed verification yet fails here breaks the bundle (spec §3.6).
    void getMapBundleSession().reportBroken()
    throw err
  }
}

async function fetchRetiredSlugs(): Promise<Readonly<Record<string, string>>> {
  try {
    const res = await fetch(mapAssetUrl('legacy-ids.json'))
    if (!res.ok) return {}
    const body = (await res.json()) as { slugs?: Record<string, string> } | null
    return body?.slugs ?? {}
  } catch {
    return {}
  }
}
